import { Button } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Label } from '@/components/common/Label';
import { Textarea } from '@/components/common/Textarea';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface ConfirmModalWithReasonProps {
    submitFunction: Function,
    text: string
}

export default observer(function ConfirmModalWithReason({ submitFunction, text }: ConfirmModalWithReasonProps) {
    // hooks
    const { t } = useTranslation();

    // store
    const { modalStore: { hideModal } } = useStore();

    // denied form schema validate
    const validateSchema = yup.object().shape({
        reason: yup.string().max(1000).required('form_error_validate.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        reason: ''
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: initialValues
    });

    // function
    const onSubmit = (data) => {
        submitFunction && submitFunction(data, () => hideModal());
    };

    const handleClickCancel = () => {
        hideModal();
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <FormField
                    control={form.control}
                    name='reason'
                    render={({ field }) => (
                        <FormItem>
                            <div className='flex w-full'>
                                <Label className='w-1/4' required>{t('words_title.reason')}</Label>
                                <FormControl className='w-3/4'>
                                    <Textarea
                                        className='resize-none min-h-[350px]'
                                        maxLength={1000}
                                        placeholder={t('placeholder.reason')}
                                        {...field}
                                    />
                                </FormControl>
                            </div>
                            <div className='flex w-full'>
                                <div className='w-1/4'></div>
                                <FormMessage className='w-3/4' />
                            </div>
                            <div className='mt-4'>{text}</div>
                        </FormItem>
                    )}
                />
                <div className='flex flex-row items-center w-full gap-4 mt-6'>
                    <Button className='w-1/2' type='button' variant='outline' onClick={handleClickCancel}>{t('button.no')}</Button>
                    <Button className='w-1/2' type='submit' variant='default'>{t('button.yes')}</Button>
                </div>
            </form>
        </Form>
    );
});
